import { z } from 'zod';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { AuthorizationIdSchema, CreditLineIdSchema, CreditPaymentIdSchema, DrawdownIdSchema, ScaChallengeIdSchema, StatementIdSchema, } from '../common/ids.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
import { paginatedSchema } from '../common/pagination.js';
import { RateSchema } from '../common/rate.js';
import { ReasonCodeSchema } from '../reason-codes.js';
import { PrepaymentModeSchema, UsuryRateSchema } from './product.js';
import { BlockReasonSchema, CreditAuthorizationStatusSchema, CreditLineStatusSchema, CreditRatingSchema, DrawdownStatusSchema, InstallmentStatusSchema, } from './states.js';
/** Referencia del comercio guardada con la autorización/utilización (`merchant_ref jsonb`). */
export const MerchantRefSchema = z
    .object({
    name: z.string(),
    aliasType: z.string(),
    aliasValue: z.string(),
    participantCode: z.string().optional(),
    mcc: z.string().optional(),
    city: z.string().optional(),
})
    .meta({ id: 'MerchantRef' });
/** Resumen de línea (equivalente a `getCardInfo`). `availableLimit = approved − used − held`. */
export const CreditLineSummarySchema = z
    .object({
    id: CreditLineIdSchema,
    productCode: z.string(),
    productName: z.string(),
    productVersion: z.int(),
    approvedLimit: MoneySchema,
    availableLimit: MoneySchema,
    usedPrincipal: MoneySchema,
    heldAmount: MoneySchema,
    creditBalance: MoneySchema,
    minimumPayment: MoneySchema,
    payoffAmount: MoneySchema,
    status: CreditLineStatusSchema,
    blockReason: BlockReasonSchema.nullable(),
    dpd: z.int().min(0),
    rating: CreditRatingSchema,
    openedAt: TimestampSchema.nullable(),
    nextCutDate: BusinessDateSchema.nullable(),
    nextDueDate: BusinessDateSchema.nullable(),
    lastStatementId: StatementIdSchema.nullable(),
    billingFrequency: z.literal('MONTHLY'),
})
    .meta({ id: 'CreditLineSummary' });
export const CreditLineProductInfoSchema = z.object({
    rateEA: RateSchema,
    rateMV: RateSchema,
    allowedInstallments: z.array(z.int()),
    fees: z.object({
        mipymeFeeRate: RateSchema,
        lifeInsuranceMonthlyRate: RateSchema,
    }),
});
/** `GET /v1/credit/lines/{lineId}`. */
export const CreditLineDetailSchema = CreditLineSummarySchema.extend({
    product: CreditLineProductInfoSchema,
    cycle: z.object({
        cutoffDate: BusinessDateSchema.nullable(),
        dueDate: BusinessDateSchema.nullable(),
    }),
}).meta({ id: 'CreditLineDetail' });
export const CreditLineListResponseSchema = z.object({ data: z.array(CreditLineSummarySchema) });
// ── Simulación ────────────────────────────────────────────────────────────
export const SimulationRequestSchema = z
    .object({
    lineId: CreditLineIdSchema,
    amount: PositiveMoneySchema,
    installments: z.int().min(1).max(120),
})
    .meta({ id: 'SimulationRequest' });
export const ScheduleRowSchema = z
    .object({
    number: z.int().min(1),
    dueDate: BusinessDateSchema,
    openingBalance: MoneySchema,
    interest: MoneySchema,
    principal: MoneySchema,
    insurance: MoneySchema,
    fee: MoneySchema,
    /** Capital + interés (cuota del sistema francés). */
    installment: MoneySchema,
    /** Cuota + seguro + comisión. */
    total: MoneySchema,
})
    .meta({ id: 'ScheduleRow' });
/** Salida de simulación (`05-core-credito.md` §5.4). Cifras calculadas solo por credit-math/Credit Core. */
export const SimulationResultSchema = z
    .object({
    amount: MoneySchema,
    installments: z.int(),
    installmentAmount: MoneySchema,
    rateEA: RateSchema,
    rateMV: RateSchema,
    moratoriumRateEA: RateSchema,
    lifeInsuranceMonthlyRate: RateSchema,
    mipymeFee: MoneySchema,
    insurance: MoneySchema,
    fees: MoneySchema,
    totalInterest: MoneySchema,
    totalInsurance: MoneySchema,
    totalFees: MoneySchema,
    totalCost: MoneySchema,
    totalToPay: MoneySchema,
    firstDueDate: BusinessDateSchema,
    schedule: z.array(ScheduleRowSchema),
    productVersion: z.int(),
    usuryRateEA: RateSchema,
    disclaimer: z.string(),
    simulatedAt: TimestampSchema,
})
    .meta({ id: 'SimulationResult' });
// ── Autorizaciones (hold) ─────────────────────────────────────────────────
export const AuthorizationResultSchema = z.enum(['APPROVED', 'DECLINED']);
export const CreditAuthorizationSchema = z
    .object({
    id: AuthorizationIdSchema,
    lineId: CreditLineIdSchema,
    amount: MoneySchema,
    installments: z.int(),
    externalRef: z.string(),
    authorizationCode: z
        .string()
        .regex(/^\d{6}$/)
        .nullable(),
    traceNumber: z.string().nullable(),
    status: CreditAuthorizationStatusSchema,
    result: AuthorizationResultSchema,
    reasonCode: ReasonCodeSchema.nullable(),
    expiresAt: TimestampSchema.nullable(),
    availableAfter: MoneySchema,
    feesPreview: MoneySchema,
    capturedDrawdownId: DrawdownIdSchema.nullable(),
})
    .meta({ id: 'CreditAuthorization' });
// ── Utilizaciones y cuotas ────────────────────────────────────────────────
export const InstallmentSchema = z
    .object({
    number: z.int().min(1),
    dueDate: BusinessDateSchema,
    openingBalance: MoneySchema,
    principal: MoneySchema,
    interest: MoneySchema,
    insurance: MoneySchema,
    fee: MoneySchema,
    total: MoneySchema,
    paidPrincipal: MoneySchema,
    paidInterest: MoneySchema,
    paidInsurance: MoneySchema,
    paidFee: MoneySchema,
    status: InstallmentStatusSchema,
    planVersion: z.int(),
})
    .meta({ id: 'Installment' });
export const DrawdownSchema = z
    .object({
    id: DrawdownIdSchema,
    lineId: CreditLineIdSchema,
    authorizationId: AuthorizationIdSchema,
    principal: MoneySchema,
    outstandingPrincipal: MoneySchema,
    installments: z.int(),
    rateEA: RateSchema,
    rateMV: RateSchema,
    merchant: MerchantRefSchema,
    externalRef: z.string(),
    status: DrawdownStatusSchema,
    disputeSince: TimestampSchema.nullable(),
    productVersion: z.int(),
    drawdownDate: BusinessDateSchema,
    schedule: z.array(InstallmentSchema),
})
    .meta({ id: 'Drawdown' });
// ── Movimientos y extractos ───────────────────────────────────────────────
export const MovementTypeSchema = z
    .enum([
    'PURCHASE',
    'PAYMENT',
    'REFUND',
    'REVERSAL',
    'INTEREST',
    'MORATORIUM',
    'INSURANCE',
    'FEE',
    'ADJUSTMENT',
])
    .meta({ id: 'MovementType' });
export const CreditMovementSchema = z
    .object({
    id: z.string(),
    type: MovementTypeSchema,
    amount: MoneySchema,
    description: z.string(),
    drawdownId: DrawdownIdSchema.nullable(),
    externalRef: z.string().nullable(),
    businessDate: BusinessDateSchema,
    postedAt: TimestampSchema,
})
    .meta({ id: 'CreditMovement' });
export const CreditMovementPageSchema = paginatedSchema(CreditMovementSchema);
export const StatementLineSchema = z.object({
    drawdownId: DrawdownIdSchema.nullable(),
    description: z.string(),
    installmentNumber: z.int().nullable(),
    principal: MoneySchema,
    interest: MoneySchema,
    insurance: MoneySchema,
    fee: MoneySchema,
    total: MoneySchema,
    excludedFromMinimum: z.boolean(),
});
/**
 * Movimiento del periodo facturado (compras, pagos, devoluciones, reversiones, ajustes).
 * CBJ SFC Parte II, Título I, Capítulo I, num. 1.2.2: descripción, fecha, valor, cuotas y tasa.
 */
export const StatementTransactionSchema = z
    .object({
    date: BusinessDateSchema,
    type: MovementTypeSchema,
    /** `CHARGE` aumenta la deuda; `CREDIT` la disminuye (pagos, devoluciones, reversiones). */
    direction: z.enum(['CHARGE', 'CREDIT']),
    description: z.string(),
    merchantName: z.string().nullable(),
    drawdownId: DrawdownIdSchema.nullable(),
    /** Referencia de la operación (p. ej. intención de pago o código Bre-B). */
    reference: z.string().nullable(),
    amount: MoneySchema,
    /** Cuotas pactadas de la compra (solo compras). */
    installments: z.int().nullable(),
    /** Tasa remuneratoria EA de la compra (solo compras). */
    rateEA: RateSchema.nullable(),
})
    .meta({ id: 'StatementTransaction' });
/**
 * Estado al corte de cada compra diferida: cuotas pactadas, canceladas y pendientes, tasa y saldo
 * (CBJ Parte II, Título I, Capítulo I, num. 1.2.2).
 */
export const StatementInstallmentPlanSchema = z
    .object({
    drawdownId: DrawdownIdSchema,
    description: z.string(),
    purchaseDate: BusinessDateSchema,
    originalAmount: MoneySchema,
    /** Cuotas pactadas (seleccionadas por el cliente). */
    installments: z.int().min(1),
    /** Cuotas totalmente pagadas al corte. */
    paidInstallments: z.int().min(0),
    /** Cuotas con saldo al corte (incluida la facturada en este extracto). */
    pendingInstallments: z.int().min(0),
    /** Número de la cuota facturada en este extracto (`null` si no factura en este ciclo). */
    billedInstallment: z.int().nullable(),
    installmentAmount: MoneySchema,
    rateEA: RateSchema,
    rateMV: RateSchema,
    /** Capital pendiente de la compra al corte. */
    outstandingPrincipal: MoneySchema,
    /** Capital que se facturará en extractos futuros (saldo diferido). */
    deferredPrincipal: MoneySchema,
    inDispute: z.boolean(),
})
    .meta({ id: 'StatementInstallmentPlan' });
/** Tasas vigentes al corte: remuneratoria, moratoria y usura certificada por la SFC. */
export const StatementRatesSchema = z
    .object({
    /** Tasa remuneratoria efectivamente aplicada: `min(pactada, usura)`. */
    rateEA: RateSchema,
    rateMV: RateSchema,
    moratoriumRateEA: RateSchema,
    moratoriumRateMV: RateSchema,
    /** Certificación de usura vigente a la fecha de corte para la modalidad del producto. */
    usury: UsuryRateSchema,
})
    .meta({ id: 'StatementRates' });
/** Cupo al corte (CBJ Parte II, Título I, Capítulo I, num. 5.1: cupo asignado y monto por utilizar). */
export const StatementCreditLimitSchema = z
    .object({
    approvedLimit: MoneySchema,
    usedPrincipal: MoneySchema,
    heldAmount: MoneySchema,
    availableLimit: MoneySchema,
})
    .meta({ id: 'StatementCreditLimit' });
export const StatementHolderSchema = z
    .object({
    fullName: z.string(),
    documentType: z.string().nullable(),
    /** Documento enmascarado (`******1222`). */
    maskedDocument: z.string().nullable(),
})
    .meta({ id: 'StatementHolder' });
/**
 * Extracto del ciclo (Credit Core). Contenido mínimo según la CBJ de la SFC, Parte II, Título I,
 * Capítulo I, nums. 1.2.2 (extractos) y 5.1 (cupo). Los montos los calcula solo el core.
 */
export const StatementSchema = z
    .object({
    id: StatementIdSchema,
    lineId: CreditLineIdSchema,
    periodStart: BusinessDateSchema,
    cutoffDate: BusinessDateSchema,
    dueDate: BusinessDateSchema,
    /** Saldo total del extracto anterior (pago total anterior). */
    openingBalance: MoneySchema,
    purchases: MoneySchema,
    /** Pagos recibidos en el periodo (pago realizado para el periodo anterior). */
    payments: MoneySchema,
    interest: MoneySchema,
    moratorium: MoneySchema,
    insurance: MoneySchema,
    fees: MoneySchema,
    minimumPayment: MoneySchema,
    /** Pago total: deja la línea a paz y salvo a la fecha de corte. */
    payoffAmount: MoneySchema,
    lines: z.array(StatementLineSchema),
    pdfUrl: z.string().nullable(),
    holder: StatementHolderSchema,
    product: z.object({ code: z.string(), name: z.string(), version: z.int() }),
    /** Valor vencido incluido en el pago mínimo y días de mora al corte. */
    pastDueAmount: MoneySchema,
    daysPastDue: z.int().min(0),
    /** Saldo a favor del cliente al corte. */
    creditBalance: MoneySchema,
    /** Capital de compras a cuotas que se facturará en extractos futuros. */
    deferredBalance: MoneySchema,
    creditLimit: StatementCreditLimitSchema,
    rates: StatementRatesSchema,
    transactions: z.array(StatementTransactionSchema),
    installmentPlans: z.array(StatementInstallmentPlanSchema),
    generatedAt: TimestampSchema,
})
    .meta({ id: 'Statement' });
// ── Pagos al crédito ──────────────────────────────────────────────────────
export const CreditPaymentTypeSchema = z.enum(['MINIMUM', 'TOTAL', 'CUSTOM', 'PREPAYMENT']);
export const PaymentAllocationSchema = z
    .object({
    interestLate: MoneySchema,
    interest: MoneySchema,
    fees: MoneySchema,
    insurance: MoneySchema,
    principal: MoneySchema,
    prepayment: MoneySchema,
    creditBalance: MoneySchema,
})
    .meta({ id: 'PaymentAllocation' });
/** `POST /v1/credit/payments` (Idem ✔). */
export const CreateCreditPaymentRequestSchema = z
    .object({
    lineId: CreditLineIdSchema,
    amount: PositiveMoneySchema,
    type: CreditPaymentTypeSchema,
    prepaymentMode: PrepaymentModeSchema.optional(),
    prepaymentDrawdownId: DrawdownIdSchema.optional(),
    sourceAccountId: z.string().min(1),
})
    .meta({ id: 'CreateCreditPaymentRequest' });
export const CreditPaymentStatusSchema = z.enum([
    'REQUIRES_SCA',
    'PROCESSING',
    'APPLIED',
    'FAILED',
    'CANCELLED',
]);
export const CreditPaymentSchema = z
    .object({
    id: CreditPaymentIdSchema,
    lineId: CreditLineIdSchema,
    amount: MoneySchema,
    type: CreditPaymentTypeSchema,
    status: CreditPaymentStatusSchema,
    scaChallengeId: ScaChallengeIdSchema.nullable(),
    allocation: PaymentAllocationSchema.nullable(),
    reasonCode: ReasonCodeSchema.nullable(),
    receivedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'CreditPayment' });
export const ConfirmWithScaRequestSchema = z
    .object({ scaToken: z.string().min(16) })
    .meta({ id: 'ConfirmWithScaRequest' });
//# sourceMappingURL=schemas.js.map